import { TransformsList, Data } from 'cio/utils/analytics/transforms';
import { isEmpty } from '@ember/utils';
import { capitalize } from '@ember/string';
import { generateFilteredTimeFrameData } from 'cio/utils/analytics/transforms/metrics';
import TransactionalMessageModel, { ContentSource } from 'cio/models/transactional-message';
import { generateSource } from '../sources';
import { Grouping } from 'cio/utils/metrics/resolution';

const GROUP_BY_LABELS: Record<string, string> = {
  [Grouping.Daily]: 'By Day',
  [Grouping.Weekly]: 'By Week',
  [Grouping.Monthly]: 'By Month',
};

const EDIT_TYPES = {
  linkTracking: 'Link tracking changed',
  hideMessageBody: 'Hide message body changed',
  queueDrafts: 'Queue messages changed',
  sendToUnsubscribed: 'Send to unsubscribed changed',
  triggerName: 'Trigger Name Changed',
  tags: 'Tags changed',
  description: 'Message description changed',
  details: 'Message details changed',
} as const;

function getSortBy(value: string | undefined) {
  if (!value) {
    return 'Last updated';
  }

  return (
    {
      name: 'Name',
      created: 'Date created',
      updated: 'Last updated',
    } as Record<string, string>
  )[value];
}

function getContentSource(source: ContentSource) {
  return {
    [ContentSource.Api]: 'API',
    [ContentSource.Template]: 'Template',
    [ContentSource.Default]: undefined,
  }[source];
}

const Transforms: TransformsList = {
  listView(data) {
    if (!data?.route) {
      return {};
    }

    let { queryParams } = data.route;

    return {
      'Filtered By Search': !isEmpty(queryParams.search),
      'Filtered By Tags': !isEmpty(queryParams.tag_ids),
      'Sorted By': getSortBy(queryParams.sort as string),
      'Sort Order': queryParams.desc ? 'Descending' : 'Ascending',
    };
  },

  overviewTabViewed(data) {
    return {
      'Transactional Content ID': data?.model?.id,
      'Transactional Channel': capitalize(data?.model?.type ?? ''),
    };
  },

  overviewSentTabViewed(data) {
    if (!data?.route) {
      return {};
    }

    return {
      'Transactional Content ID': data.model?.id,
      'Transactional Channel': capitalize(data.model?.type ?? ''),
      'Filter By Status': capitalize((data.route.queryParams.deliveryStatus as string) ?? ''),
      'Filter By Date': !isEmpty(data.route.queryParams.startTs),
    };
  },

  setupStepViewed(data: Data & { model?: TransactionalMessageModel }) {
    if (!data?.model) {
      return {};
    }

    return {
      'Transactional Content ID': data.model.id,
      'Transactional Channel': capitalize(data.model.type),
      'Transactional Content Type': getContentSource(data.model.contentSource),
    };
  },

  codeCopied(data: Data & { source: 'code_snippet' | 'install_snippet' }) {
    if (!data?.model) {
      return {};
    }

    return {
      'Transactional Content ID': data.model.id,
      'Transactional Channel': capitalize(data.model.type),
      Language: data.language,
      'Content Copied': {
        code_snippet: 'Code Snippet',
        install_snippet: 'Install Library',
      }[data.source],
    };
  },

  addContentStarted(data) {
    if (!data?.model) {
      return {};
    }

    return {
      'Transactional Content ID': data.model.message.id,
      'Transactional Channel': capitalize(data.model.message.type),
    };
  },

  editStarted(data: Data & { editType: keyof typeof EDIT_TYPES }) {
    if (!data?.model) {
      return {};
    }

    return {
      'Transactional Content ID': data.model.id,
      'Transactional Channel': capitalize(data.model.type),
      'Edit Type': EDIT_TYPES[data.editType],
    };
  },

  editContentStarted(data) {
    if (!data?.model) {
      return {};
    }

    return {
      'Message Used In': 'Transactional Message',
      'Message Type': capitalize(data.model.message.type),
      'Transactional Content ID': data.model.message.id,
    };
  },

  metricsDisplayOptionChanged(data: Data & { editType: 'metrics' | 'chartUnit'; chartUnit: 'count' | 'percentage' }) {
    return {
      'Edit Type': {
        metrics: 'Metrics Lines Changed',
        chartUnit: 'Chart Unit Changed',
      }[data.editType],
      'Chart Unit': (
        {
          percentage: 'Percentage',
          count: 'Count',
        } as Record<string, string>
      )[data.chartUnit],
      Metrics: data.metrics,
    };
  },

  timeframeFiltersChanged(data: Data & { editType: 'grouping' | 'timeframe' }) {
    return {
      ...generateFilteredTimeFrameData(data),
      'Group By Value': GROUP_BY_LABELS[data.groupBy],
      'Edit Type': {
        grouping: 'Group By',
        timeframe: 'Timeframe',
      }[data.editType],
      Source: generateSource(data),
    };
  },

  deleteStarted(data) {
    if (!data?.model) {
      return {};
    }

    return {
      'Transactional Content ID': data.model.id,
      'Transactional Channel': capitalize(data.model.type),
    };
  },

  messageCopyStarted(data) {
    if (!data?.model) {
      return {};
    }

    return {
      'Transactional Content ID': data.model.id,
      'Transactional Channel': capitalize(data.model.type),
      'Transactional Content Type': getContentSource(data.model.contentSource),
      'Hide Message Body': data.model.hideMessageBody,
      'Link Tracking': data.model.linkTracking,
      'Queue As Drafts': data.model.queueDrafts,
      'Send To Unsubscribed': data.model.sendToUnsubscribed,
    };
  },

  activated(data: Data & { model: TransactionalMessageModel }) {
    if (!data?.model) {
      return {};
    }

    return {
      'Transactional Content ID': data.model.id,
      'Transactional Channel': capitalize(data.model.type),
      'Transactional Content Type': getContentSource(data.model.contentSource),
      'Hide Message Body': data.model.hideMessageBody,
      'Link Tracking': data.model.linkTracking,
      'Queue As Drafts': data.model.queueDrafts,
      'Send To Unsubscribed': data.model.sendToUnsubscribed,
    };
  },
};

export default Transforms;
